import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Globe, Building2, Search, Handshake, Check, ArrowUpRight, ArrowDown } from "lucide-react";
import { LazyImage } from "@/components/site/LazyImage";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { Ribbon } from "@/components/site/Ribbon";
import { IMAGES } from "@/config/images.config";

const TRADE = [
  { icon: Globe, no: "01", title: "Export Trade", text: "We connect premium Indian textiles and products with buyers across the globe — managing quality, documentation and reliable delivery from factory floor to final port." },
  { icon: Building2, no: "02", title: "Domestic Trade", text: "Serving Indian buyers with dependable bulk supply and competitive pricing, backed by decades of hands-on manufacturing know-how." },
  { icon: Search, no: "03", title: "Sourcing & Supply", text: "Factory-direct sourcing through trusted supplier networks, tailored precisely to your specifications, volumes and timelines." },
  { icon: Handshake, no: "04", title: "Business Partnerships", text: "Long-term relationships with distributors, hotels and institutions seeking a committed, professional trade partner from India." },
];

const WHY = [
  "Quality-focused sourcing",
  "Reliable supplier networks",
  "Competitive trade solutions",
  "Customized buyer requirements",
  "Professional order coordination",
  "Long-term business relationships",
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayO = useTransform(scrollYProgress, [0, 1], [0.38, 0.78]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[86svh] min-h-[560px] w-full overflow-hidden bg-navy md:h-[100svh] md:min-h-[640px]" data-testid="hero-section">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={IMAGES.home.hero} alt="Premium terry towels" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayO }} className="absolute inset-0 bg-navy" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-navy/10" />

        <div className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-end px-6 pb-20 md:px-12 lg:px-16 lg:pb-28">
          <p
            className="bwt-rise mb-6 font-display text-xs font-semibold uppercase tracking-[0.34em] text-sky md:text-sm"
            style={{ animationDelay: "0.15s" }}
          >
            Export • Domestic • Sourcing • Supply
          </p>

          <h1 className="font-display font-black leading-[0.9] tracking-tighter text-white text-[clamp(2rem,9.5vw,9rem)]">
            <MaskReveal lines={["BRAHMANDNAYAK", "WORLD TRADE"]} delay={0.35} />
          </h1>

          <p
            className="bwt-rise mt-8 max-w-2xl font-display text-xl font-light text-white/85 md:text-3xl"
            style={{ animationDelay: "0.7s" }}
          >
            India to the World. Built for Global Trade.
          </p>
        </div>

        <div className="absolute bottom-8 right-6 z-10 hidden items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/50 md:flex md:right-12 lg:right-16">
          Scroll <ArrowDown className="h-4 w-4 animate-bounce" />
        </div>
      </section>

      <Ribbon dark />

      {/* INTRO */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32 lg:px-16 lg:py-40" data-testid="intro-section">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-24">
          <Reveal>
            <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-sky">Who we are</p>
            <div className="mt-6 h-px w-24 bg-navy/20" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-display text-2xl font-light leading-snug tracking-tight text-navy md:text-4xl">
              Brahmandnayak World Trade connects <span className="text-sky">Indian products with global markets</span> — exporting quality goods from India while serving domestic buyers with reliable sourcing and bulk supply. Our mission is to make international and domestic trade simple, reliable and professional.
            </p>
          </Reveal>
        </div>
      </section>

      {/* OUR TRADE — bento */}
      <section className="bg-navy px-6 py-24 text-white md:px-12 md:py-32 lg:px-16 lg:py-40" data-testid="our-trade-section">
        <div className="mx-auto max-w-[1600px]">
          <Reveal className="mb-16 max-w-2xl">
            <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-sky">Our Trade</p>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-6xl">Four pillars. One partner.</h2>
          </Reveal>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-12">
            {TRADE.map((t, i) => {
              const Icon = t.icon;
              const span = i === 0 ? "lg:col-span-7" : i === 1 ? "lg:col-span-5" : i === 2 ? "lg:col-span-5" : "lg:col-span-7";
              return (
                <Reveal key={t.no} delay={i * 0.08} className={span}>
                  <div className="group flex h-full flex-col justify-between rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition-colors duration-500 hover:border-sky/50 hover:bg-white/[0.06] md:p-12" data-testid={`trade-card-${t.no}`}>
                    <div className="flex items-start justify-between">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/15 text-sky transition-colors duration-500 group-hover:border-sky group-hover:bg-sky group-hover:text-navy">
                        <Icon className="h-6 w-6" />
                      </span>
                      <span className="font-display text-5xl font-black text-white/10">{t.no}</span>
                    </div>
                    <div className="mt-10">
                      <h3 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{t.title}</h3>
                      <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60 md:text-base">{t.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY BWT */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32 lg:px-16 lg:py-40" data-testid="why-section">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-24">
          <Reveal>
            <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-sky">Why Brahmandnayak World Trade?</p>
            <h2 className="mt-5 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-navy md:text-6xl">
              Global Reach.<br />Indian Supply.<br /><span className="text-sky">Trusted Trade.</span>
            </h2>
            <div className="mt-10 overflow-hidden rounded-3xl">
              <LazyImage src={IMAGES.home.why} alt="Textile manufacturing" className="aspect-[16/10]" imgClassName="grayscale transition-all duration-700 hover:grayscale-0" />
            </div>
          </Reveal>

          <div className="flex flex-col justify-center">
            {WHY.map((w, i) => (
              <Reveal key={w} delay={i * 0.06}>
                <div className="group flex items-center gap-5 border-b border-black/10 py-6" data-testid={`why-item-${i}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy text-white transition-colors duration-300 group-hover:bg-sky">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="font-display text-xl font-semibold tracking-tight text-navy md:text-2xl">{w}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA BAND */}
      <section className="relative overflow-hidden bg-sky px-6 py-24 md:px-12 md:py-32 lg:px-16" data-testid="closing-cta">
        <div className="mx-auto max-w-[1600px]">
          <Reveal>
            <p className="font-display text-sm font-bold uppercase tracking-[0.24em] text-navy/70">From India. For the World.</p>
            <h2 className="mt-5 max-w-4xl font-display text-4xl font-black leading-[0.98] tracking-tighter text-navy md:text-7xl">
              Your Global Trade Partner from India.
            </h2>
            <div className="mt-12 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
              <Link to="/products" data-testid="closing-cta-products" className="inline-flex items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">
                Explore Our Products <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" data-testid="closing-cta-requirement" className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/30 px-8 py-4 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-navy hover:text-white">
                Send Your Requirement
              </Link>
            </div>
          </Reveal>
        </div>
        <span aria-hidden className="pointer-events-none absolute -bottom-10 right-4 select-none font-display text-[22vw] font-black leading-none text-white/20">↗</span>
      </section>
    </div>
  );
}
